import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, BookOpen, LogIn, Library, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "/livrum-logo-light.webp";

const formatCurrency = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const MetaRow = ({ label, value, testId }) => value ? (
  <div style={{ display: 'flex', gap: '0.75rem', padding: '0.6rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
    <span style={{ width: '120px', flexShrink: 0, color: '#94a3b8', fontSize: '0.85rem' }}>{label}</span>
    <span style={{ color: '#e2e8f0', fontSize: '0.95rem' }} data-testid={testId}>{value}</span>
  </div>
) : null;

const BookLanding = ({ editorId, editorSlug, bookId, notice }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/digital/${editorId}/catalog`)
      .then((r) => setData(r.data))
      .catch(() => setError('Biblioteca não encontrada'));
  }, [editorId]);

  if (error) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#e2e8f0' }}>{error}</div>;
  if (!data) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}><Loader2 className="w-10 h-10 animate-spin" style={{ color: '#68AAB0' }} /></div>;

  const book = data.books.find((b) => b.id === bookId);
  const redirect = `/${editorSlug}/livro/${bookId}`;
  const cover = book?.capa_url && (book.capa_url.startsWith('http') ? book.capa_url : `${BACKEND_URL}${book.capa_url}`);

  return (
    <div data-testid="book-landing" style={{ minHeight: '100vh', background: '#0f172a', color: 'white', display: 'flex', flexDirection: 'column' }}>
      <div style={{ maxWidth: '1240px', width: '100%', margin: '0 auto', padding: '3rem 1.5rem', flex: 1 }}>
        <button onClick={() => navigate(`/${editorSlug}`)} data-testid="landing-catalog-link" style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2rem' }}>
          <Library className="w-4 h-4" /> {data.editor?.nome || 'Catálogo'}
        </button>
        {notice && (
          <div data-testid="landing-notice" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', background: 'rgba(245,158,11,0.15)', color: '#fbbf24', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '2rem' }}>
            <AlertCircle className="w-4 h-4" /> {notice}
          </div>
        )}
        {!book ? <p data-testid="landing-book-not-found">Livro não encontrado nesta biblioteca.</p> : (
          <div className="book-landing-grid">
            <div style={{ width: '100%', aspectRatio: '190 / 275', borderRadius: '10px', overflow: 'hidden', background: 'linear-gradient(110deg, #1e293b 30%, #273449 50%, #1e293b 70%)', boxShadow: '0 30px 60px rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {cover ? <img src={cover} alt={book.titulo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} data-testid="landing-cover" /> : <BookOpen className="w-16 h-16" style={{ color: '#475569' }} />}
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 className="text-4xl sm:text-5xl" style={{ fontWeight: 700, lineHeight: 1.1, marginBottom: '0.75rem' }} data-testid="landing-title">{book.titulo}</h1>
              <p style={{ color: '#68AAB0', fontSize: '1.1rem', marginBottom: '1.5rem' }} data-testid="landing-author">{book.autor}</p>
              <MetaRow label="Edição/Ano" value={book.ano_publicacao} testId="landing-year" />
              <MetaRow label="ISBN/ISSN" value={book.isbn} testId="landing-isbn" />
            </div>
            <div style={{ background: '#1e293b', position: 'sticky', top: '2rem', borderRadius: '14px', padding: '1.75rem', border: '1px solid rgba(104,170,176,0.35)' }}>
              <Button onClick={() => navigate(`/${editorSlug}/login?action=subscribe&redirect=${encodeURIComponent(redirect)}`)} data-testid="landing-subscribe-btn"
                style={{ width: '100%', height: '56px', fontSize: '1.05rem', fontWeight: 700, background: '#F28C28', color: 'white', transition: 'transform 0.15s ease' }}>
                Assine esta biblioteca!
              </Button>
              <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '1.5rem', fontWeight: 700 }} data-testid="landing-price">
                {data.editor?.plataforma_publica ? <span style={{ fontSize: '1.1rem', color: '#5eead4' }}>Gratuita · só cadastro</span> : data.valor_assinatura ? <>{formatCurrency(data.valor_assinatura)}<span style={{ fontSize: '0.9rem', fontWeight: 400, color: '#94a3b8' }}>/mês</span></> : <span style={{ fontSize: '1rem', color: '#94a3b8' }}>Preço sob consulta</span>}
              </p>
              <p style={{ textAlign: 'center', fontSize: '0.9rem' }}>
                <a href={`/${editorSlug}`} onClick={(e) => { e.preventDefault(); navigate(`/${editorSlug}`); }} data-testid="landing-book-count"
                  style={{ color: '#68AAB0', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                  {data.total} {data.total === 1 ? 'livro disponível' : 'livros disponíveis'}
                </a>
              </p>
              <button onClick={() => navigate(`/${editorSlug}/login?redirect=${encodeURIComponent(redirect)}`)} data-testid="landing-login-btn"
                style={{ width: '100%', marginTop: '1.5rem', background: 'none', border: '1px solid #475569', color: '#e2e8f0', borderRadius: '8px', padding: '0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <LogIn className="w-4 h-4" /> Já sou assinante
              </button>
            </div>
          </div>
        )}
      </div>
      <footer data-testid="landing-footer" style={{ padding: '1.5rem', textAlign: 'center', background: '#1a2332' }}>
        <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', color: 'white', textDecoration: 'none' }} data-testid="landing-footer-logo-link">
          <span style={{ fontSize: '0.85rem', opacity: 0.85 }} data-testid="landing-footer-developed-by">Desenvolvido por</span>
          <img src={LOGO_URL} alt="Livrum" style={{ height: '26px', width: 'auto', filter: 'brightness(0) invert(1)' }} />
        </a>
      </footer>
    </div>
  );
};

export default BookLanding;
